<template>
  <!--
    Feuille du reçu de paiement.

    Trois modèles, reprenant exactement la maquette validée :
      • Classique — format A4/A5 administratif, en-tête souligné, tableau ;
      • Ticket    — reçu étroit, style caisse (rouleau 80 mm) ;
      • Moderne   — bandeau de couleur, montants en trois colonnes.

    La feuille a une largeur FIXE en pixels (celle de la maquette). C'est le
    conteneur qui la met à l'échelle, à l'écran comme à l'impression : le
    document reste donc rigoureusement identique quelle que soit la place
    disponible, au lieu de se réagencer selon la largeur.
  -->
  <div
    class="rc-sheet"
    :class="[`rc-${data.template}`, `rc-format-${data.format}`]"
    :style="paletteStyle"
  >
    <!-- ========================= Modèle Classique ========================= -->
    <template v-if="data.template === 'classique'">
      <header class="rc-head">
        <img v-if="logo" class="rc-logo" :src="logo" :alt="$t('payment_receipt.logo_alt')" />
        <div v-else-if="data.show_logo" class="rc-logo rc-logo-empty" aria-hidden="true"></div>

        <div class="rc-head-main">
          <h1 class="rc-school">{{ data.school.name || placeholder }}</h1>
          <p class="rc-school-line" v-if="data.show_school_contacts && data.school.address">
            {{ data.school.address }}
          </p>
          <p class="rc-school-line" v-if="data.show_school_contacts && contactLine">
            {{ contactLine }}
          </p>
        </div>

        <div class="rc-head-side">
          <p class="rc-doc-kind">{{ data.title }}</p>
          <p class="rc-doc-number">{{ data.number }}</p>
        </div>
      </header>

      <div class="rc-body">
        <div class="rc-meta-grid">
          <div class="rc-meta" v-for="item in documentMeta" :key="item.label">
            <p class="rc-meta-label">{{ item.label }}</p>
            <p class="rc-meta-value" :class="{ 'rc-meta-empty': !item.value }">
              {{ item.value || placeholder }}
            </p>
          </div>
        </div>

        <section class="rc-student-box">
          <h2 class="rc-section-title">{{ $t("payment_receipt.student_section") }}</h2>
          <div class="rc-meta-grid rc-meta-grid-3">
            <div class="rc-meta" v-for="item in studentMeta" :key="item.label">
              <p class="rc-meta-label">{{ item.label }}</p>
              <p class="rc-meta-value" :class="{ 'rc-meta-empty': !item.value }">
                {{ item.value || placeholder }}
              </p>
            </div>
          </div>
        </section>

        <table class="rc-table">
          <thead>
            <tr>
              <th class="rc-th">{{ $t("payment_receipt.col_description") }}</th>
              <th class="rc-th rc-right">{{ $t("payment_receipt.col_amount") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr class="rc-tr">
              <td class="rc-td">{{ $t("payment_receipt.row_due", { type: data.type }) }}</td>
              <td class="rc-td rc-right">{{ money(data.amount_due) }}</td>
            </tr>
            <tr class="rc-tr rc-tr-zone">
              <td class="rc-td rc-medium">{{ $t("payment_receipt.row_paid") }}</td>
              <td class="rc-td rc-right rc-accent rc-semibold">{{ money(data.amount_paid) }}</td>
            </tr>
            <tr>
              <td class="rc-td rc-semibold">{{ $t("payment_receipt.row_balance") }}</td>
              <td class="rc-td rc-right rc-bold" :class="data.settled ? 'rc-accent' : 'rc-danger'">
                {{ money(data.balance) }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="rc-foot">
          <p class="rc-note">{{ data.footer_note }}</p>
          <div class="rc-sign" v-if="data.show_signature">
            <p class="rc-sign-role">{{ data.signatory_role }}</p>
            <div class="rc-sign-line">{{ $t("payment_receipt.signature_line") }}</div>
            <p class="rc-sign-name" v-if="data.signatory_name">{{ data.signatory_name }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- =========================== Modèle Ticket =========================== -->
    <template v-else-if="data.template === 'ticket'">
      <img v-if="logo" class="rc-logo rc-logo-center" :src="logo" :alt="$t('payment_receipt.logo_alt')" />
      <div
        v-else-if="data.show_logo"
        class="rc-logo rc-logo-center rc-logo-empty"
        aria-hidden="true"
      ></div>

      <h1 class="rc-ticket-title">{{ data.title }}</h1>
      <p class="rc-ticket-sub">{{ data.school.name || placeholder }}</p>
      <p class="rc-ticket-sub">{{ data.issued_on }}</p>

      <div class="rc-dashed"></div>

      <div class="rc-ticket-lines">
        <p class="rc-ticket-line">
          <span class="rc-bold">{{ $t("payment_receipt.field_number") }}:</span> {{ data.number }}
        </p>
        <p class="rc-ticket-line">
          <span class="rc-bold">{{ $t("payment_receipt.field_type") }}:</span> {{ data.type }}
        </p>
        <p class="rc-ticket-line">
          <span class="rc-bold">{{ $t("payment_receipt.field_student") }}:</span>
          {{ data.student_name || placeholder }}
        </p>
        <p class="rc-ticket-line">
          <span class="rc-bold">{{ $t("payment_receipt.field_registration") }}:</span>
          {{ data.registration_number || placeholder }}
        </p>
        <p class="rc-ticket-line">
          <span class="rc-bold">{{ $t("payment_receipt.field_classroom") }}:</span>
          {{ data.classroom || placeholder }}
        </p>
        <p class="rc-ticket-line" v-if="data.reference">
          <span class="rc-bold">{{ $t("payment_receipt.field_transaction") }}:</span>
          {{ data.reference }}
        </p>
        <p class="rc-ticket-line">
          <span class="rc-bold">{{ $t("payment_receipt.row_due_short") }}:</span>
          {{ money(data.amount_due) }}
        </p>
        <p class="rc-ticket-line">
          <span class="rc-bold">{{ $t("payment_receipt.row_balance") }}:</span>
          {{ money(data.balance) }}
        </p>
      </div>

      <div class="rc-dashed"></div>

      <p class="rc-ticket-total">
        {{ $t("payment_receipt.row_paid_short") }}: {{ money(data.amount_paid) }}
      </p>
      <p class="rc-ticket-status">
        {{ data.settled ? $t("payment_receipt.status_settled") : $t("payment_receipt.status_partial") }}
      </p>
      <template v-if="data.show_signature">
        <p class="rc-ticket-signatory">{{ data.signatory_role }}</p>
        <p class="rc-ticket-signatory-name" v-if="data.signatory_name">
          {{ data.signatory_name }}
        </p>
      </template>
    </template>

    <!-- ========================== Modèle Moderne ========================== -->
    <template v-else>
      <div class="rc-banner">
        <div class="rc-banner-row">
          <div class="rc-banner-left">
            <img
              v-if="logo"
              class="rc-logo rc-logo-soft"
              :src="logo"
              :alt="$t('payment_receipt.logo_alt')"
            />
            <div
              v-else-if="data.show_logo"
              class="rc-logo rc-logo-soft rc-logo-empty"
              aria-hidden="true"
            ></div>
            <div>
              <h1 class="rc-banner-school">{{ data.school.name || placeholder }}</h1>
              <p class="rc-banner-line" v-if="data.show_school_contacts && data.school.address">
                {{ data.school.address }}
              </p>
              <p class="rc-banner-line" v-if="data.show_school_contacts && contactLine">
                {{ contactLine }}
              </p>
            </div>
          </div>
          <div class="rc-banner-right">
            <p class="rc-banner-kind">{{ data.type }}</p>
            <p class="rc-banner-amount">{{ money(data.amount_paid) }}</p>
            <p class="rc-banner-line">{{ data.number }}</p>
          </div>
        </div>
      </div>

      <div class="rc-modern-grid">
        <div class="rc-modern-col">
          <div class="rc-meta" v-for="item in modernLeftMeta" :key="item.label">
            <p class="rc-meta-label">{{ item.label }}</p>
            <p class="rc-meta-value" :class="{ 'rc-meta-empty': !item.value }">
              {{ item.value || placeholder }}
            </p>
          </div>
        </div>
        <div class="rc-modern-col">
          <div class="rc-meta" v-for="item in modernRightMeta" :key="item.label">
            <p class="rc-meta-label">{{ item.label }}</p>
            <p class="rc-meta-value" :class="{ 'rc-meta-empty': !item.value }">
              {{ item.value || placeholder }}
            </p>
          </div>
        </div>
      </div>

      <div class="rc-amounts">
        <div class="rc-amount-cell">
          <p class="rc-amount-label">{{ $t("payment_receipt.row_due_short") }}</p>
          <p class="rc-amount-value">{{ money(data.amount_due) }}</p>
        </div>
        <div class="rc-amount-cell">
          <p class="rc-amount-label">{{ $t("payment_receipt.row_paid_short") }}</p>
          <p class="rc-amount-value rc-accent">{{ money(data.amount_paid) }}</p>
        </div>
        <div class="rc-amount-cell">
          <p class="rc-amount-label">{{ $t("payment_receipt.row_balance") }}</p>
          <p
            class="rc-amount-value rc-bold"
            :class="data.settled ? 'rc-accent' : 'rc-danger'"
          >
            {{ money(data.balance) }}
          </p>
        </div>
      </div>

      <div class="rc-foot rc-foot-modern">
        <p class="rc-note">{{ data.footer_note }}</p>
        <div class="rc-sign" v-if="data.show_signature">
          <p class="rc-sign-role">{{ data.signatory_role }}</p>
          <div class="rc-sign-line">{{ $t("payment_receipt.signature_line") }}</div>
          <p class="rc-sign-name" v-if="data.signatory_name">{{ data.signatory_name }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { formatAmount, paletteOf } from "@/utils/receiptModel";
import { schoolProfile } from "@/utils/schoolProfile";

export default {
  name: "payment-receipt-sheet",
  props: {
    /** Reçu déjà résolu par `resolveReceipt()` : que des données réelles. */
    data: { type: Object, required: true },
  },
  computed: {
    /**
     * Remplaçant d'une valeur absente.
     *
     * Un tiret de remplissage plutôt qu'un blanc : le document reste lisible
     * et l'information manquante saute aux yeux, au lieu de passer pour une
     * ligne volontairement vide.
     */
    placeholder() {
      return "—————";
    },
    /** Métadonnées du document (modèle Classique). */
    documentMeta() {
      const rows = [
        { label: this.$t("payment_receipt.field_number"), value: this.data.number },
        { label: this.$t("payment_receipt.field_type"), value: this.data.type },
        { label: this.$t("payment_receipt.field_issued_on"), value: this.data.issued_on },
        { label: this.$t("payment_receipt.field_reference"), value: this.data.reference },
      ];
      if (this.data.show_signature) {
        rows.push({ label: this.$t("payment_receipt.field_signatory"), value: this.signatory });
      }
      return rows;
    },
    /** Informations de l'élève, communes aux modèles Classique et Moderne. */
    studentMeta() {
      return [
        { label: this.$t("payment_receipt.field_student"), value: this.data.student_name },
        {
          label: this.$t("payment_receipt.field_registration"),
          value: this.data.registration_number,
        },
        { label: this.$t("payment_receipt.field_classroom"), value: this.data.classroom },
      ];
    },
    /** Colonne de gauche du modèle Moderne. */
    modernLeftMeta() {
      return [
        ...this.studentMeta,
        { label: this.$t("payment_receipt.field_issued_on"), value: this.data.issued_on },
      ];
    },
    /** Colonne de droite du modèle Moderne. */
    modernRightMeta() {
      const rows = [
        { label: this.$t("payment_receipt.field_type"), value: this.data.type },
        { label: this.$t("payment_receipt.field_reference"), value: this.data.reference },
      ];
      if (this.data.show_signature) {
        rows.push({ label: this.$t("payment_receipt.field_signatory"), value: this.signatory });
      }
      return rows;
    },
    palette() {
      return paletteOf(this.data.palette);
    },
    paletteStyle() {
      return {
        "--rc-paper": this.palette.paper,
        "--rc-ink": this.palette.ink,
        "--rc-soft": this.palette.soft,
        "--rc-accent": this.palette.accent,
        "--rc-accent-ink": this.palette.accent_ink,
        "--rc-line": this.palette.line,
        "--rc-zone": this.palette.zone,
        "--rc-danger": this.palette.danger,
      };
    },
    /**
     * Logo de l'établissement.
     *
     * Il provient de la fiche établissement et n'est pas un réglage du reçu :
     * il n'est ni saisi ici, ni enregistré avec le modèle. Le reçu résolu peut
     * le porter (génération hors ligne du profil), sinon on retombe sur le
     * profil chargé.
     */
    logo() {
      if (!this.data.show_logo) return "";
      return (this.data.school && this.data.school.logo) || schoolProfile.logo || "";
    },
    contactLine() {
      return [this.data.school.phone, this.data.school.email].filter(Boolean).join(" · ");
    },
    /**
     * Ligne « Signataire » de la grille d'informations : le nom s'il est
     * renseigné, la fonction sinon. Le pied du document, lui, porte toujours
     * les deux — la fonction au-dessus du trait, le nom en dessous.
     */
    signatory() {
      return this.data.signatory_name || this.data.signatory_role || "";
    },
  },
  methods: {
    money(value) {
      return formatAmount(value, this.data.locale || this.$i18n.locale, this.data.currency);
    },
  },
};
</script>

<style scoped>
/* -------------------------------------------------------------------------
   Feuille — largeur fixe, mise à l'échelle par le conteneur.
   ---------------------------------------------------------------------- */
.rc-sheet {
  background: var(--rc-paper);
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  line-height: 1.5;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
}

.rc-classique,
.rc-moderne {
  width: 768px;
}

/* A5 : même document, légèrement resserré — comme la maquette. */
.rc-classique.rc-format-a5,
.rc-moderne.rc-format-a5 {
  width: 576px;
  font-size: 0.92em;
}

.rc-moderne {
  border-radius: 12px;
  overflow: hidden;
}

.rc-ticket {
  width: 320px;
  padding: 32px 24px;
}

/* --- Éléments partagés --------------------------------------------------- */

.rc-right {
  text-align: right;
}

.rc-medium {
  font-weight: 500;
}

.rc-semibold {
  font-weight: 600;
}

.rc-bold {
  font-weight: 700;
}


.rc-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  flex: 0 0 auto;
}

.rc-logo-empty {
  border: 1px dashed var(--rc-line);
  border-radius: 8px;
}

.rc-meta-label {
  font-size: 12px;
  margin: 0;
}

.rc-meta-value {
  font-weight: 500;
  margin: 0;
  font-size: 14px;
}

.rc-meta-empty {
  letter-spacing: 1px;
}

/* -------------------------------------------------------------------------
   Modèle Classique
   ---------------------------------------------------------------------- */
.rc-head {
  display: flex;
  align-items: center;
  gap: 20px;
  border-bottom: 4px solid var(--rc-accent);
  padding: 24px 32px;
}

.rc-head-main {
  flex: 1;
  min-width: 0;
}

.rc-school {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.025em;
  margin: 0;
  line-height: 1.25;
}

.rc-school-line {
  font-size: 14px;
  margin: 0;
}

.rc-head-side {
  text-align: right;
  flex: 0 0 auto;
}

.rc-doc-kind {
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin: 0;
}

.rc-doc-number {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.rc-body {
  padding: 24px 32px;
}

.rc-meta-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  font-size: 14px;
}

.rc-student-box {
  margin-top: 24px;
  border: 1px solid var(--rc-line);
  background: var(--rc-zone);
  border-radius: 8px;
  padding: 16px;
}

.rc-section-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.rc-meta-grid-3 {
  margin-top: 8px;
  gap: 12px;
}

.rc-table {
  margin-top: 24px;
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.rc-th {
  background: var(--rc-accent);
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
}

.rc-th.rc-right {
  text-align: right;
}

.rc-td {
  padding: 12px 16px;
}

.rc-tr {
  border-bottom: 1px solid var(--rc-line);
}

.rc-tr-zone {
  background: var(--rc-zone);
}

.rc-foot {
  margin-top: 40px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
}

.rc-note {
  max-width: 320px;
  font-size: 12px;
  margin: 0;
}

.rc-sign {
  text-align: center;
  flex: 0 0 auto;
}

.rc-sign-role {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.rc-sign-line {
  margin-top: 40px;
  border-top: 1px solid var(--rc-soft);
  padding-top: 4px;
  font-size: 12px;
}

/* Nom du signataire, sous le trait : la fonction reste au-dessus, l'espace
   entre les deux est celui où l'on signe. */
.rc-sign-name {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 500;
}

/* -------------------------------------------------------------------------
   Modèle Ticket
   ---------------------------------------------------------------------- */
.rc-logo-center {
  width: 64px;
  height: 64px;
  margin: 0 auto;
  display: block;
}

.rc-ticket-title {
  margin: 12px 0 0;
  text-align: center;
  font-size: 20px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.025em;
}

.rc-ticket-sub {
  text-align: center;
  font-size: 14px;
  margin: 0;
}

.rc-dashed {
  margin: 16px 0;
  border-top: 1px dashed var(--rc-line);
}

.rc-ticket-lines > .rc-ticket-line + .rc-ticket-line {
  margin-top: 4px;
}

.rc-ticket-line {
  font-size: 14px;
  margin: 0;
  word-break: break-word;
}

.rc-ticket-total {
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  margin: 0;
}

.rc-ticket-status {
  margin: 16px 0 0;
  text-align: center;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.rc-ticket-signatory {
  margin: 16px 0 0;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
}

.rc-ticket-signatory-name {
  margin: 2px 0 0;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
}

/* -------------------------------------------------------------------------
   Modèle Moderne
   ---------------------------------------------------------------------- */
.rc-banner {
  background: var(--rc-accent);
  padding: 32px;
}

.rc-banner-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.rc-banner-left {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.rc-logo-soft {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px;
}

.rc-banner-school {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  line-height: 1.3;
}

.rc-banner-line {
  font-size: 14px;
  margin: 0;
}

.rc-banner-right {
  text-align: right;
  flex: 0 0 auto;
}

.rc-banner-kind {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0;
}

.rc-banner-amount {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
}

.rc-modern-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  padding: 32px;
}

.rc-modern-col > .rc-meta + .rc-meta {
  margin-top: 16px;
}

.rc-amounts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  background: var(--rc-line);
}

.rc-amount-cell {
  background: var(--rc-paper);
  padding: 20px 32px;
}

.rc-amount-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.rc-amount-value {
  margin: 4px 0 0;
  font-size: 18px;
  font-weight: 600;
}

.rc-foot-modern {
  margin-top: 0;
  padding: 24px 32px;
}

/* -------------------------------------------------------------------------
   Couleurs
   -------------------------------------------------------------------------
   Le reçu est un DOCUMENT posé sur du papier blanc, pas un élément
   d'interface : il doit garder ses propres couleurs, y compris à l'intérieur
   du thème sombre de l'application.

   Or ce thème impose aux éléments d'interface :
     • `.dark-version h1…h6      { color: #fff !important }`
     • `.dark-version .card p    { color: #fff !important; opacity: .6 }`
     • `.dark-version .card span { color: #fff }`
   La feuille étant rendue dans une carte, son texte devenait blanc sur blanc,
   donc invisible.

   D'où ces deux passes :
     1. un reset qui rend l'héritage à tous les descendants — ils reprennent
        alors l'encre de la feuille ;
     2. les exceptions (gris doux, accent, rouge, texte sur bandeau), posées
        APRÈS pour l'emporter sur le reset.

   `.rc-sheet` est répétée TROIS fois pour hisser la spécificité au-dessus de
   `.dark-version .card p`. `!important` ne suffirait pas seul : à importance
   égale, c'est la spécificité qui départage, et le compte se fait catégorie
   par catégorie. Le sélecteur du thème vaut (0,2,1) — deux classes et un
   élément ; `*` ne comptant pour rien, deux répétitions n'auraient donné que
   (0,2,0), qui perd sur le nombre d'éléments. Trois donnent (0,3,0), qui
   l'emporte, et (0,4,0) pour les exceptions ci-dessous.
   ---------------------------------------------------------------------- */

.rc-sheet.rc-sheet.rc-sheet {
  color: var(--rc-ink) !important;
}

.rc-sheet.rc-sheet.rc-sheet * {
  color: inherit !important;
  opacity: 1 !important;
}

/* Gris doux : libellés, mentions secondaires, valeurs absentes. */
.rc-sheet.rc-sheet.rc-sheet .rc-school-line,
.rc-sheet.rc-sheet.rc-sheet .rc-meta-label,
.rc-sheet.rc-sheet.rc-sheet .rc-meta-empty,
.rc-sheet.rc-sheet.rc-sheet .rc-section-title,
.rc-sheet.rc-sheet.rc-sheet .rc-note,
.rc-sheet.rc-sheet.rc-sheet .rc-sign-line,
.rc-sheet.rc-sheet.rc-sheet .rc-ticket-sub,
.rc-sheet.rc-sheet.rc-sheet .rc-ticket-status,
.rc-sheet.rc-sheet.rc-sheet .rc-amount-label {
  color: var(--rc-soft) !important;
}

/* Couleur d'accent : mention « Reçu », montants réglés. */
.rc-sheet.rc-sheet.rc-sheet .rc-accent,
.rc-sheet.rc-sheet.rc-sheet .rc-doc-kind {
  color: var(--rc-accent) !important;
}

/* Solde restant dû. */
.rc-sheet.rc-sheet.rc-sheet .rc-danger {
  color: var(--rc-danger) !important;
}

/* Textes posés SUR la couleur d'accent : bandeau et en-tête de tableau.
   Les descendants du bandeau héritent de cette couleur via le reset. */
.rc-sheet.rc-sheet.rc-sheet .rc-banner,
.rc-sheet.rc-sheet.rc-sheet .rc-th {
  color: var(--rc-accent-ink) !important;
}

/* Lignes secondaires du bandeau : atténuées, mais lisibles. */
.rc-sheet.rc-sheet.rc-sheet .rc-banner-line,
.rc-sheet.rc-sheet.rc-sheet .rc-banner-kind {
  opacity: 0.8 !important;
}

/* À l'impression l'ombre portée sort en aplat gris : elle est retirée. */
@media print {
  .rc-sheet {
    box-shadow: none;
  }
}
</style>
